import { reachedStage } from './prospectFilters';
import type { Prospect, ProspectFunnelRow } from './prospects';

export type ProspectDmStage =
  'messaged' | 'replied' | 'info_reply' | 'overview_sent' | 'promoted' | 'call_booked';
const stages: {
  key: ProspectDmStage;
  label: string;
  explainer: string;
  matches: (row: ProspectFunnelRow) => boolean;
}[] = [
  {
    key: 'messaged',
    label: 'Messaged',
    explainer: 'At least one DM was logged for this Tier 2 prospect.',
    matches: (row) => (row.dm_count ?? 0) > 0,
  },
  {
    key: 'replied',
    label: 'Replied',
    explainer: 'A reply or info reply was recorded.',
    matches: (row) => row.replied_at !== null || row.info_reply_at !== null,
  },
  {
    key: 'info_reply',
    label: 'Info reply',
    explainer: 'Asked for the overview. Also counts as Replied.',
    matches: (row) => row.info_reply_at !== null,
  },
  {
    key: 'overview_sent',
    label: 'Overview sent',
    explainer: 'The overview was sent.',
    matches: (row) => row.overview_sent_at !== null,
  },
  {
    key: 'promoted',
    label: 'Promoted to lead',
    explainer: 'Became a lead. Earlier steps remain counted.',
    matches: (row) => row.promoted_at !== null,
  },
  {
    key: 'call_booked',
    label: 'Call booked',
    explainer: 'A booked call was recorded.',
    matches: (row) => row.call_booked_at !== null,
  },
];

/** Activity only. A later step does not invent skipped earlier milestones. */
export function buildProspectDmFunnel(rows: readonly ProspectFunnelRow[]) {
  const messaged = rows.filter((row) => row.tier === 'tier2' && (row.dm_count ?? 0) > 0);
  const tagged = rows.filter((row) => row.tier === 'tier2');
  return {
    tagged: tagged.length,
    notMessaged: tagged.filter((row) => (row.dm_count ?? 0) === 0).length,
    stages: stages.map((stage) => {
      const ids = messaged
        .filter(stage.matches)
        .flatMap((row) => (row.prospect_id === null ? [] : [row.prospect_id]));
      return {
        key: stage.key,
        label: stage.label,
        explainer: stage.explainer,
        ids,
        count: ids.length,
        percent: messaged.length === 0 ? 0 : Math.round((ids.length / messaged.length) * 100),
      };
    }),
  };
}

export function prospectDmTaggedLine(funnel: { tagged: number; notMessaged: number }): string {
  return `Tagged: ${String(funnel.tagged)} · Not messaged yet: ${String(funnel.notMessaged)}`;
}

export type Tier1Stage =
  | 'requested'
  | 'accepted'
  | 'dm1'
  | 'dm2'
  | 'dm3'
  | 'replied'
  | 'call_booked'
  | 'call_held'
  | 'proposal_sent'
  | 'won';
export interface Tier1JourneyEvidence {
  prospect_id: string | null;
  call_held_at: string | null;
  proposal_sent_at: string | null;
  won: boolean;
}
const tier1Statuses = [
  ['requested', 'Request sent', 'pending', 1],
  ['accepted', 'Accepted', 'connected', 1],
  ['dm1', 'DM 1', 'pitched', 1],
  ['dm2', 'DM 2', 'dm_2', 2],
  ['dm3', 'DM 3', 'dm_3', 3],
] as const;

/** Status steps need a reached status and enough logged DMs for that step. */
export function buildTier1Funnel(
  prospects: readonly Pick<Prospect, 'id' | 'tier' | 'status'>[],
  rows: readonly ProspectFunnelRow[],
  journeys: readonly Tier1JourneyEvidence[] = [],
) {
  const tagged = prospects.filter((prospect) => prospect.tier === 'tier1');
  const taggedIds = new Set(tagged.map((prospect) => prospect.id));
  const tierRows = rows.filter(
    (row) => row.tier === 'tier1' && row.prospect_id !== null && taggedIds.has(row.prospect_id),
  );
  const active = tierRows.filter((row) => (row.dm_count ?? 0) > 0);
  const dmCounts = new Map(active.map((row) => [row.prospect_id, row.dm_count ?? 0]));
  const evidence = new Map(journeys.map((row) => [row.prospect_id, row]));
  const activity: {
    key: Tier1Stage;
    label: string;
    matches: (row: ProspectFunnelRow) => boolean;
  }[] = [
    {
      key: 'replied',
      label: 'Replied',
      matches: (row) => row.replied_at !== null || row.info_reply_at !== null,
    },
    { key: 'call_booked', label: 'Call booked', matches: (row) => row.call_booked_at !== null },
    {
      key: 'call_held',
      label: 'Call held',
      matches: (row) => evidence.get(row.prospect_id)?.call_held_at != null,
    },
    {
      key: 'proposal_sent',
      label: 'Proposal',
      matches: (row) => evidence.get(row.prospect_id)?.proposal_sent_at != null,
    },
    { key: 'won', label: 'Won', matches: (row) => evidence.get(row.prospect_id)?.won === true },
  ];
  const stage = (key: Tier1Stage, label: string, ids: string[]) => ({
    key,
    label,
    ids,
    count: ids.length,
    percent: tagged.length === 0 ? 0 : Math.round((ids.length / tagged.length) * 100),
    explainer:
      key === 'dm2' || key === 'dm3'
        ? `This person has at least ${key === 'dm2' ? 'two' : 'three'} logged DMs and a stored status that reached or passed this step.`
        : key === 'requested' || key === 'accepted' || key === 'dm1'
          ? 'This person has a logged DM and a stored status that reached or passed this step.'
          : 'This person has a logged DM and a recorded step.',
  });
  return {
    tagged: tagged.length,
    notMessaged: tierRows.filter((row) => (row.dm_count ?? 0) === 0).length,
    stages: [
      ...tier1Statuses.map(([key, label, status, minimumDms]) =>
        stage(
          key,
          label,
          tagged
            .filter((p) => (dmCounts.get(p.id) ?? 0) >= minimumDms && reachedStage(p, status))
            .map((p) => p.id),
        ),
      ),
      ...activity.map((item) =>
        stage(
          item.key,
          item.label,
          active
            .filter(item.matches)
            .flatMap((row) => (row.prospect_id === null ? [] : [row.prospect_id])),
        ),
      ),
    ],
  };
}
